export const TAB = {
  init: function () {
    Tabs.initAll('[data-tabs]')
  },
}

class Tabs {
  constructor(root) {
    this.root = root

    this.onClick = this.onClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)

    // fade
    this.fadeMs = this.getFadeMs()
    this._fadeTid = 0
    this._pendingHide = null
    this._rafIn = 0

    // scroll
    this.scrollEnabled = this.root.hasAttribute('data-tabs-scroll')

    const off = parseInt(this.root.getAttribute('data-tabs-scroll-offset') || '0', 10)
    this.scrollOffset = isNaN(off) ? 0 : off
  }

  static initAll(selector) {
    const roots = document.querySelectorAll(selector)
    const created = []

    for (let i = 0; i < roots.length; i += 1) {
      const root = roots[i]
      if (root.__tabsInstance) continue
      root.__tabsInstance = true

      root.dataset.tabsInitialized = 'true'

      const ins = new Tabs(root)
      ins.init()
      Tabs._instances.push(ins)
      created.push(ins)
    }

    if (!Tabs._boundResize) {
      Tabs._boundResize = true

      let tid = null
      window.addEventListener('resize', function () {
        clearTimeout(tid)
        tid = setTimeout(function () {
          for (let j = 0; j < Tabs._instances.length; j += 1) {
            Tabs._instances[j].refresh()
          }
        }, 120)
      })
    }

    return created
  }

  init() {
    this.activation =
      (this.root.getAttribute('data-activation') || 'auto').toLowerCase() === 'manual'
        ? 'manual'
        : 'auto'

    this.collect()
    if (!this.tabs.length || !this.panels.length) return

    this.root.addEventListener('click', this.onClick)
    this.root.addEventListener('keydown', this.onKeydown)

    const initial =
      this.findFirstActiveTabByClass() || this.findFirstActiveTabByAria() || this.tabs[0]

    // 初回はアニメ無し
    this.activate(initial, false, true, false)
  }

  refresh() {
    this.collect()
    if (!this.tabs.length || !this.panels.length) return

    const current =
      this.findFirstActiveTabByAria() || this.findFirstActiveTabByClass() || this.tabs[0]

    if (current) this.activate(current, false, true, false)
  }

  collect() {
    this.tabLists = this.toArray(this.root.querySelectorAll('[role="tablist"], .c-tabs__nav'))

    this.tabs = this.toArray(this.root.querySelectorAll('[role="tab"]'))
    if (!this.tabs.length) this.tabs = this.toArray(this.root.querySelectorAll('.c-tabs__nav-item'))

    this.panels = this.toArray(this.root.querySelectorAll('[role="tabpanel"]'))
    if (!this.panels.length)
      this.panels = this.toArray(this.root.querySelectorAll('.c-tabs__panel-item'))

    for (let i = 0; i < this.tabLists.length; i += 1) {
      if (!this.tabLists[i].getAttribute('role')) this.tabLists[i].setAttribute('role', 'tablist')
    }
    for (let j = 0; j < this.tabs.length; j += 1) {
      if (!this.tabs[j].getAttribute('role')) this.tabs[j].setAttribute('role', 'tab')
      if (!this.tabs[j].hasAttribute('aria-selected'))
        this.tabs[j].setAttribute('aria-selected', 'false')
      if (!this.tabs[j].hasAttribute('tabindex')) this.tabs[j].setAttribute('tabindex', '0')
    }
    for (let k = 0; k < this.panels.length; k += 1) {
      if (!this.panels[k].getAttribute('role')) this.panels[k].setAttribute('role', 'tabpanel')
      this.panels[k].classList.remove('is-fading-out')
      this.panels[k].classList.remove('is-fading-in')
    }
  }

  toArray(nodeList) {
    return Array.prototype.slice.call(nodeList)
  }

  closest(el, selector) {
    const proto = Element.prototype
    const matches = proto.matches || proto.msMatchesSelector || proto.webkitMatchesSelector

    while (el && el.nodeType === 1) {
      if (matches.call(el, selector)) return el
      el = el.parentElement
    }
    return null
  }

  prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  scrollToPanel(panel) {
    if (!this.scrollEnabled) return
    if (!panel) return

    const reduce = this.prefersReducedMotion()
    const behavior = reduce ? 'auto' : 'smooth'

    const rect = panel.getBoundingClientRect()
    const top = window.pageYOffset + rect.top - this.scrollOffset

    try {
      window.scrollTo({ top, behavior })
    } catch (e) {
      window.scrollTo(0, top)
    }
  }

  findFirstActiveTabByClass() {
    for (let i = 0; i < this.tabs.length; i += 1) {
      if (this.tabs[i].classList.contains('is-active')) return this.tabs[i]
    }
    return null
  }

  findFirstActiveTabByAria() {
    for (let i = 0; i < this.tabs.length; i += 1) {
      if (this.tabs[i].getAttribute('aria-selected') === 'true') return this.tabs[i]
    }
    return null
  }

  getTabListForTab(tab) {
    return this.closest(tab, '[role="tablist"]') || this.closest(tab, '.c-tabs__nav') || null
  }

  getTabsInList(listEl) {
    if (!listEl) return this.tabs

    let a = this.toArray(listEl.querySelectorAll('[role="tab"]'))
    if (!a.length) a = this.toArray(listEl.querySelectorAll('.c-tabs__nav-item'))
    return a
  }

  getPanelIdFromTab(tab) {
    let id =
      tab.getAttribute('aria-controls') ||
      tab.getAttribute('data-controls') ||
      tab.getAttribute('data-target') ||
      ''

    id = (id || '').replace(/^\s+|\s+$/g, '')
    if (id.charAt(0) === '#') id = id.slice(1)

    if (!id) {
      const href = tab.getAttribute('href')
      if (href && href.indexOf('#') !== -1) id = href.slice(href.indexOf('#') + 1)
    }

    return id
  }

  getPanelForTab(tab) {
    const id = this.getPanelIdFromTab(tab)
    if (id) {
      const panel = document.getElementById(id)
      if (panel) return panel
    }
    const idx = this.tabs.indexOf(tab)
    if (idx > -1 && this.panels[idx]) return this.panels[idx]
    return null
  }

  getFadeMs() {
    // CSSが0.3sならデフォルト300msに合わせる
    const v = this.root.getAttribute('data-fade')
    const n = parseInt(v, 10)
    if (!isNaN(n)) return n
    return 300
  }

  getCurrentPanel() {
    for (let i = 0; i < this.panels.length; i += 1) {
      const p = this.panels[i]
      if (p.getAttribute('aria-hidden') === 'false' && !p.hasAttribute('hidden')) return p
    }
    return null
  }

  cancelPending() {
    if (this._fadeTid) {
      clearTimeout(this._fadeTid)
      this._fadeTid = 0
    }
    if (this._rafIn) {
      cancelAnimationFrame(this._rafIn)
      this._rafIn = 0
    }
    if (this._pendingHide) {
      this._pendingHide.classList.remove('is-fading-out')
      this._pendingHide = null
    }
  }

  activate(tab, shouldFocus, skipAnim, shouldScroll) {
    if (!tab) return
    if (this.tabs.indexOf(tab) === -1) return

    const nextPanel = this.getPanelForTab(tab)
    if (!nextPanel) return

    this.cancelPending()

    const currentPanel = this.getCurrentPanel()

    const doAnim = !skipAnim && !this.prefersReducedMotion() && this.fadeMs > 0

    // タブ更新（全tabsで1つ）
    for (let i = 0; i < this.tabs.length; i += 1) {
      const t = this.tabs[i]
      const isActive = t === tab
      t.classList.toggle('is-active', isActive)
      t.setAttribute('aria-selected', isActive ? 'true' : 'false')
    }

    // tabindex（tablist分割でも各tablistに1つ0）
    const lists = this.tabLists.length ? this.tabLists : [this.root]
    for (let li = 0; li < lists.length; li += 1) {
      const listEl = lists[li]
      const tabsInList = this.getTabsInList(listEl)
      if (!tabsInList.length) continue

      const focusable = tabsInList.indexOf(tab) !== -1 ? tab : tabsInList[0]
      for (let ti = 0; ti < tabsInList.length; ti += 1) {
        tabsInList[ti].setAttribute('tabindex', tabsInList[ti] === focusable ? '0' : '-1')
      }
    }

    // まず次パネルを表示準備（hidden解除＆aria）
    nextPanel.classList.remove('is-fading-out')
    nextPanel.removeAttribute('hidden')
    nextPanel.setAttribute('aria-hidden', 'false')

    // ここが肝：hidden→復帰でも必ずフェードイン
    if (doAnim) {
      nextPanel.classList.add('is-active')
      nextPanel.classList.add('is-fading-in') // opacity:0（CSSで上書き）

      // 次フレームで is-fading-in を外して opacity:1へ
      const self = this
      this._rafIn = requestAnimationFrame(function () {
        self._rafIn = 0
        nextPanel.classList.remove('is-fading-in')
      })
    } else {
      nextPanel.classList.add('is-active')
      nextPanel.classList.remove('is-fading-in')
    }

    // 旧パネルの扱い
    if (currentPanel && currentPanel !== nextPanel) {
      if (doAnim) {
        currentPanel.classList.add('is-fading-out')
        currentPanel.classList.remove('is-active')

        this._pendingHide = currentPanel

        const self2 = this
        this._fadeTid = setTimeout(function () {
          if (!self2._pendingHide) return

          self2._pendingHide.setAttribute('hidden', '')
          self2._pendingHide.setAttribute('aria-hidden', 'true')
          self2._pendingHide.classList.remove('is-fading-out')
          self2._pendingHide = null
          self2._fadeTid = 0
        }, this.fadeMs + 40)
      } else {
        currentPanel.setAttribute('hidden', '')
        currentPanel.setAttribute('aria-hidden', 'true')
        currentPanel.classList.remove('is-fading-out')
        currentPanel.classList.remove('is-active')
      }
    }

    // その他パネルは確実にhidden
    for (let pj = 0; pj < this.panels.length; pj += 1) {
      const p = this.panels[pj]
      if (p === nextPanel) continue
      if (doAnim && p === this._pendingHide) continue

      p.setAttribute('hidden', '')
      p.setAttribute('aria-hidden', 'true')
      p.classList.remove('is-active')
      p.classList.remove('is-fading-in')
      // is-fading-out は pending 以外は消してOK
      if (!doAnim || p !== this._pendingHide) p.classList.remove('is-fading-out')
    }

    if (shouldScroll) {
      const p = nextPanel
      const self3 = this
      requestAnimationFrame(function () {
        self3.scrollToPanel(p)
      })
    }

    if (shouldFocus) tab.focus()
  }

  setRovingTabindex(listEl, focusTab) {
    const tabsInList = this.getTabsInList(listEl)
    if (!tabsInList.length) return

    for (let i = 0; i < tabsInList.length; i += 1) {
      tabsInList[i].setAttribute('tabindex', tabsInList[i] === focusTab ? '0' : '-1')
    }
  }

  onClick(e) {
    const tab =
      this.closest(e.target, '[role="tab"]') || this.closest(e.target, '.c-tabs__nav-item')
    if (!tab || !this.root.contains(tab)) return

    e.preventDefault()
    this.activate(tab, true, false, true)
  }

  onKeydown(e) {
    const tab =
      this.closest(e.target, '[role="tab"]') || this.closest(e.target, '.c-tabs__nav-item')
    if (!tab || !this.root.contains(tab)) return

    const key = e.key
    const listEl = this.getTabListForTab(tab)
    const tabsInList = this.getTabsInList(listEl)
    const idx = tabsInList.indexOf(tab)
    if (idx < 0) return

    let nextIdx = -1

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault()
      nextIdx = (idx + 1) % tabsInList.length
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault()
      nextIdx = (idx - 1 + tabsInList.length) % tabsInList.length
    } else if (key === 'Home') {
      e.preventDefault()
      nextIdx = 0
    } else if (key === 'End') {
      e.preventDefault()
      nextIdx = tabsInList.length - 1
    } else if (this.activation === 'manual' && (key === 'Enter' || key === ' ')) {
      e.preventDefault()
      this.activate(tab, true, false, true)
      return
    } else {
      return
    }

    const next = tabsInList[nextIdx]
    if (!next) return

    if (this.activation === 'auto') {
      this.activate(next, true, false, true)
    } else {
      this.setRovingTabindex(listEl, next)
      next.focus()
    }
  }
}

Tabs._instances = []
Tabs._boundResize = false
